import { ACTION_STEP_NOTIFICATION_TYPES } from '@doss/common-all'
import { Theme } from '@mui/material'
import { IconType } from 'react-icons'
import { BsExclamationLg, BsInfo, BsX } from 'react-icons/bs'
import { RxCheck } from 'react-icons/rx'
import * as yup from 'yup'

export type NotificationIconAndColor = {
  icon: IconType
  color: string
}

export type NotificationColorClasses = {
  bg: string
  text: string
  dot: string
}

/**
 * Get the icon and color for a notification type in MUI theme format
 * Used by event.tsx for activity timeline events
 */
export function getNotificationIconAndColor(
  notificationType: ACTION_STEP_NOTIFICATION_TYPES,
  theme: Theme,
): NotificationIconAndColor {
  switch (notificationType) {
    case ACTION_STEP_NOTIFICATION_TYPES.GREEN_CHECK:
      return { icon: RxCheck, color: theme.palette.common.doss.green[10] }
    case ACTION_STEP_NOTIFICATION_TYPES.YELLOW_ALERT:
      return { icon: BsExclamationLg, color: theme.palette.common.doss.yellow[10] }
    case ACTION_STEP_NOTIFICATION_TYPES.RED_ERROR:
      return { icon: BsX, color: theme.palette.common.doss.red[10] }
    case ACTION_STEP_NOTIFICATION_TYPES.GRAY_INFO:
      return { icon: BsInfo, color: theme.palette.common.doss.gray[10] }
  }
}

/**
 * Get the icon component for a notification type
 * Used by ActionStepNotification.tsx for notification panel items
 */
export function getNotificationIcon(notificationType: ACTION_STEP_NOTIFICATION_TYPES): IconType {
  switch (notificationType) {
    case ACTION_STEP_NOTIFICATION_TYPES.GREEN_CHECK:
      return RxCheck
    case ACTION_STEP_NOTIFICATION_TYPES.YELLOW_ALERT:
      return BsExclamationLg
    case ACTION_STEP_NOTIFICATION_TYPES.RED_ERROR:
      return BsX
    case ACTION_STEP_NOTIFICATION_TYPES.GRAY_INFO:
      return BsInfo
  }
}

/**
 * Get the Tailwind CSS color classes for a notification type
 * Used by ActionStepNotification.tsx for notification panel items
 */
export function getNotificationColorClasses(
  notificationType: ACTION_STEP_NOTIFICATION_TYPES,
): NotificationColorClasses {
  switch (notificationType) {
    case ACTION_STEP_NOTIFICATION_TYPES.GREEN_CHECK:
      return { bg: 'bg-green-3', text: 'text-green-9', dot: 'bg-green-9' }
    case ACTION_STEP_NOTIFICATION_TYPES.YELLOW_ALERT:
      return {
        bg: 'bg-yellow-3',
        text: 'text-yellow-10',
        dot: 'bg-yellow-10',
      }
    case ACTION_STEP_NOTIFICATION_TYPES.RED_ERROR:
      return { bg: 'bg-red-3', text: 'text-red-9', dot: 'bg-red-9' }
    case ACTION_STEP_NOTIFICATION_TYPES.GRAY_INFO:
      return { bg: 'bg-gray-3', text: 'text-gray-9', dot: 'bg-gray-9' }
  }
}

// Schema for action step notification metadata stored in the Notification table
export const actionStepNotificationMetadataSchema = yup
  .object({
    title: yup.string().required(),
    message: yup.string().required(),
    notificationType: yup.string().oneOf(Object.values(ACTION_STEP_NOTIFICATION_TYPES)).required(),
    linkToPath: yup.string().nullable().optional(),
    linkToType: yup.string().nullable().optional(),
  })
  .required()
